import { CHECKLIST_SECTIONS, mockOriginalAudit, FINDING_TYPE_LABELS } from '../constants';

/** Maps legacy stored values to API `findingType` enum */
const LEGACY_FINDING_TYPE_MAP = {
  'Best Practice': 'BestPractice',
};

export const normalizeFindingType = findingType => {
  if (findingType == null || findingType === '') return 'Finding';
  return LEGACY_FINDING_TYPE_MAP[findingType] || findingType;
};

export const findingTypeDisplayLabel = findingType => {
  const normalized = normalizeFindingType(findingType);
  return FINDING_TYPE_LABELS[normalized] || normalized;
};

export const createChecklistState = sections =>
  (sections || CHECKLIST_SECTIONS).reduce((sectionAcc, section) => {
    const sectionKey = String(section.id ?? section.key);
    const sectionItems = section.questions || section.items || [];
    const itemsState = sectionItems.reduce((itemAcc, item) => {
      const questionKey = String(item.id ?? item);
      return {
        ...itemAcc,
        [questionKey]: { answer: '', comment: '' },
      };
    }, {});

    return {
      ...sectionAcc,
      [sectionKey]: itemsState,
    };
  }, {});

/** YYYY-MM-DD for the current local calendar day (used when adding a finding). */
export const getTodayDateInputValue = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const toDateInputValue = value => {
  if (!value) return '';
  if (typeof value === 'string' && value.length >= 10) {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const FINDING_PROGRESS_FIELDS = ['type', 'issuedDate', 'targetDate', 'shortDescription'];

export const isTextFilled = value =>
  value !== null && value !== undefined && String(value).trim() !== '';

export const isFindingFieldFilled = (finding, field) => isTextFilled(finding && finding[field]);

export const countFindingProgress = (findings = []) => {
  const total = findings.length * FINDING_PROGRESS_FIELDS.length;
  const completed = findings.reduce(
    (acc, finding) =>
      acc + FINDING_PROGRESS_FIELDS.filter(field => isFindingFieldFilled(finding, field)).length,
    0,
  );
  return { completed, total };
};

export const createFindingEntry = () => ({
  id: `${Date.now()}-${Math.random()}`,
  type: 'Finding',
  shortDescription: '',
  issuedDate: getTodayDateInputValue(),
  targetDate: '',
  comment: '',
  detailedDescription: '',
});

export const buildSummaryText = ({
  auditConductedWeek,
  findingsCount,
  recommendationsCount,
  bestPracticesCount,
  commentsCount = 0,
}) => {
  const year = auditConductedWeek ? new Date(auditConductedWeek).getFullYear() : new Date().getFullYear();
  const dateText = auditConductedWeek || '[DATE]';
  return `During the ${year} PQF/PSM Audit, there were ${findingsCount} findings, ${recommendationsCount} recommendations, ${bestPracticesCount} best practices, and ${commentsCount} comment(s) identified. This audit was conducted on ${dateText}.`;
};

export const createFollowUpEntries = () =>
  mockOriginalAudit.findings.map(item => ({
    ...item,
    status: 'StillPending',
    completionDate: '',
    completionNotes: '',
  }));

export const buildFollowUpSummary = ({ followUpDate, correctedCount, pendingCount }) => {
  const dateText = followUpDate || '[FOLLOW-UP DATE]';
  return `Follow-up review completed on ${dateText}. ${correctedCount} finding(s) are corrected and ${pendingCount} finding(s) remain pending correction.`;
};

export const buildDefaultAuditName = companyName => {
  const label = (companyName || '').trim();
  return label ? `${label} PSM Audit` : 'PSM Audit';
};

export const buildDefaultFollowUpName = companyName => {
  const label = (companyName || '').trim();
  return label ? `${label} Audit Follow-up` : 'Audit Follow-up';
};
